/**
 * The one HTTP entry point for the whole app.
 *
 *   - unwraps API Specification §2.2's `{ data }` / `{ data, meta }` envelope
 *   - turns §2.3's `{ error }` body into a typed {@link ApiError}
 *   - attaches the bearer access token, refreshing it BEFORE sending when it
 *     is missing (a fresh page load keeps it in memory only) or about to
 *     expire, so a normal session never produces a 401
 *   - refreshing is single-flight in this tab and serialised across tabs
 *     (Web Locks), because the API revokes a session whose refresh token is
 *     replayed, and two tabs refreshing together would replay it
 *   - on a 401 anyway (revoked, clock skew) rotates the refresh token ONCE
 *     and retries; if that fails, clears the session so the auth layer can
 *     redirect to login
 *
 * Nothing else in the app calls `fetch` directly.
 */
import type { ApiErrorBody, TokenResponse } from '@mervonn/shared-types';
import { API_BASE_URL } from './config';
import { ApiError, NetworkError } from './errors';
import { tokenStore } from '../../features/auth/tokenStore';

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  /** Skip the bearer token + 401-refresh machinery (login, refresh itself). */
  anonymous?: boolean;
  /** `X-Case-Reference` — required by the Privileged Access Service for a
   * platform operator accessing a specific tenant's data (API Spec §3.4). */
  caseReference?: string;
  signal?: AbortSignal;
}

interface Enveloped<T> {
  data: T;
  meta?: unknown;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined) params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function parseBody(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/** A single fetch with no auth-retry logic. */
async function rawFetch(path: string, opts: RequestOptions, accessToken: string | null): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  // A FormData body (file upload) is sent as-is: the browser sets the multipart boundary itself.
  const isForm = typeof FormData !== 'undefined' && opts.body instanceof FormData;
  if (opts.body !== undefined && !isForm) headers['Content-Type'] = 'application/json';
  if (accessToken && !opts.anonymous) headers['Authorization'] = `Bearer ${accessToken}`;
  if (opts.caseReference) headers['X-Case-Reference'] = opts.caseReference;

  try {
    return await fetch(buildUrl(path, opts.query), {
      method: opts.method ?? 'GET',
      headers,
      body: opts.body === undefined ? undefined : isForm ? (opts.body as FormData) : JSON.stringify(opts.body),
      signal: opts.signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new NetworkError(err);
  }
}

let refreshInFlight: Promise<boolean> | null = null;
let lastRefreshAt = 0;

/** Refresh when the token has less than this left. */
const REFRESH_LEAD_MS = 30_000;
/** Never refresh ahead of expiry twice within this window: a wrong device clock must not cause a refresh storm. */
const MIN_REFRESH_GAP_MS = 10_000;

/** A token to send: the current one, or a fresh one when it is missing or about to expire. */
async function currentAccessToken(): Promise<string | null> {
  const token = tokenStore.getAccessToken();
  const expiresAt = tokenStore.getAccessExpiresAt();
  const usable = token !== null && (expiresAt === null || expiresAt - Date.now() > REFRESH_LEAD_MS);
  if (usable) return token;
  if (!tokenStore.getRefreshToken()) return token;
  if (token !== null && Date.now() - lastRefreshAt < MIN_REFRESH_GAP_MS) return token;
  await refreshSession();
  return tokenStore.getAccessToken();
}

/**
 * Rotate the refresh token. Deduped in this tab: concurrent callers share one call. Across tabs the call
 * runs under a lock and reads the stored refresh token only once it holds it, so a tab never sends a token
 * another tab has just rotated.
 */
function refreshSession(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  const attempt: Promise<boolean> = locks
    ? Promise.resolve(locks.request('mw-refresh-session', rotateRefreshToken))
    : rotateRefreshToken();
  const shared = attempt.finally(() => {
    refreshInFlight = null;
  });
  refreshInFlight = shared;
  return shared;
}

async function rotateRefreshToken(): Promise<boolean> {
  const refreshToken = tokenStore.getRefreshToken();
  if (!refreshToken) return false;
  try {
    const res = await rawFetch(
      '/auth/refresh',
      { method: 'POST', body: { refresh_token: refreshToken }, anonymous: true },
      null,
    );
    if (!res.ok) {
      tokenStore.clear();
      return false;
    }
    const body = (await parseBody(res)) as Enveloped<TokenResponse> | undefined;
    if (!body?.data?.access_token || !body.data.refresh_token) {
      tokenStore.clear();
      return false;
    }
    tokenStore.setTokens(body.data.access_token, body.data.refresh_token);
    lastRefreshAt = Date.now();
    return true;
  } catch {
    tokenStore.clear();
    return false;
  }
}

async function toApiError(res: Response): Promise<ApiError> {
  const body = (await parseBody(res)) as ApiErrorBody | undefined;
  return new ApiError(res.status, body?.error, `Request failed (${res.status})`);
}

/**
 * Make an API call. Returns the unwrapped `data`. For a paginated endpoint,
 * use {@link apiList} to also get `meta`.
 */
export async function apiRequest<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { body } = await apiRequestEnveloped<T>(path, opts);
  return body.data;
}

/** Every real paginated endpoint in this app (`GET /employees`, `GET /devices`,
 * …) returns this same `meta` shape — `employees.service.ts`/`devices.service.ts`'s
 * own `{ page, per_page, total }` convention. */
export interface ListMeta {
  page: number;
  per_page: number;
  total: number;
}

export async function apiList<T>(
  path: string,
  opts: RequestOptions = {},
): Promise<{ data: T[]; meta: ListMeta }> {
  const { body } = await apiRequestEnveloped<T[]>(path, opts);
  return { data: body.data, meta: body.meta as ListMeta };
}

/**
 * For an endpoint whose handler returns its own `{ data, ...extra }` (the live punch feed's `cursor`): the
 * server leaves such a body untouched, so the whole body is the result, not just its `data`.
 */
export async function apiPage<TPage>(path: string, opts: RequestOptions = {}): Promise<TPage> {
  const { body } = await apiRequestEnveloped<unknown>(path, opts);
  return body as unknown as TPage;
}

/**
 * Download a binary response (e.g. the report ZIP). Same auth + single
 * refresh-and-retry as every other call; an error body is parsed into an
 * {@link ApiError} exactly like `apiRequest`. Returns the blob and the
 * server-suggested filename, if any.
 */
export async function apiBlob(
  path: string,
  opts: RequestOptions = {},
): Promise<{ blob: Blob; filename: string | null }> {
  let res = await rawFetch(path, opts, opts.anonymous ? null : await currentAccessToken());
  if (res.status === 401 && !opts.anonymous) {
    const refreshed = await refreshSession();
    if (refreshed) res = await rawFetch(path, opts, tokenStore.getAccessToken());
  }
  if (!res.ok) throw await toApiError(res);
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const match = disposition.match(/filename="?([^";]+)"?/i);
  return { blob: await res.blob(), filename: match ? match[1] : null };
}

async function apiRequestEnveloped<T>(
  path: string,
  opts: RequestOptions,
): Promise<{ body: Enveloped<T> }> {
  let res = await rawFetch(path, opts, opts.anonymous ? null : await currentAccessToken());

  if (res.status === 401 && !opts.anonymous) {
    const refreshed = await refreshSession();
    if (refreshed) {
      res = await rawFetch(path, opts, tokenStore.getAccessToken());
    }
  }

  if (!res.ok) throw await toApiError(res);

  const parsed = (await parseBody(res)) as Enveloped<T> | undefined;
  // A 204 / empty 200 still needs a shape for callers.
  return { body: parsed ?? ({ data: null as unknown as T } as Enveloped<T>) };
}
